"use client";

import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from "@headlessui/react";
import { Check, ChevronDown } from "lucide-react";

export type SelectOption = { value: string; label: string };

/** Accessible, styled dropdown (Headless UI) — a nicer replacement for <select>. */
export default function Select({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: SelectOption[];
}) {
  const current = options.find((o) => o.value === value);

  return (
    <Listbox value={value} onChange={onChange}>
      <div className="relative">
        <ListboxButton className="flex w-full items-center justify-between gap-3 border border-black/15 bg-transparent p-3 text-start text-sm text-ash outline-none transition-colors focus:border-accent data-[open]:border-accent dark:border-white/15 dark:text-white">
          <span className="truncate">{current?.label}</span>
          <ChevronDown className="h-4 w-4 shrink-0 text-ash/50 transition-transform duration-200 group-data-[open]:rotate-180 dark:text-white/50" />
        </ListboxButton>
        <ListboxOptions
          anchor="bottom start"
          transition
          className="z-[70] mt-1 w-[var(--button-width)] origin-top border border-black/10 bg-white shadow-2xl transition duration-150 ease-out [--anchor-gap:4px] focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0 dark:border-white/10 dark:bg-[#1c1c1c]"
        >
          {options.map((o) => (
            <ListboxOption
              key={o.value}
              value={o.value}
              className="flex cursor-pointer items-center justify-between gap-3 px-4 py-3 text-sm text-ash transition-colors data-[focus]:bg-accent/10 data-[selected]:text-accent dark:text-white"
            >
              <span className="truncate">{o.label}</span>
              <Check className="h-4 w-4 shrink-0 text-accent opacity-0 group-data-[selected]:opacity-100 data-[selected]:opacity-100" />
            </ListboxOption>
          ))}
        </ListboxOptions>
      </div>
    </Listbox>
  );
}
